import { useDebounce } from "@core/utils/debounce/useDebounce";
import { useWorkspaceContext } from "@core/context";
import type {
  TMemberProject,
  TMemberProjectListData,
  TMemberProjectQueryParams,
  TMemberProjectStatus,
} from "@modules/project/portal/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getMemberProjects } from "@modules/project/portal/services";

const DEV_MOCK = false;
const LIMIT = 10;

export const memberProjectsKeys = {
  all: (workspaceId: string) => ["member-projects", workspaceId] as const,
  list: (workspaceId: string, params: object) =>
    ["member-projects", workspaceId, params] as const,
};

export function useGetMemberProjects() {
  const { workspaceId } = useWorkspaceContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sortBy, setSortByState] = useState<string>("joined_at");
  const [sortOrder, setSortOrderState] = useState<"asc" | "desc">("desc");

  const activeTab = (searchParams.get("tab") ?? "active") as
    | "active"
    | "archived";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const rawSearch = searchParams.get("search") ?? "";
  const debouncedSearch = useDebounce(rawSearch, 300);
  const statusParam = searchParams.get("status") as TMemberProjectStatus | null;

  const setActiveTab = (tab: "active" | "archived") =>
    setSearchParams((p) => {
      p.set("tab", tab);
      p.set("page", "1");
      return p;
    });

  const setPage = (value: number) =>
    setSearchParams((p) => {
      p.set("page", String(value));
      return p;
    });

  const setSearch = (value: string) =>
    setSearchParams((p) => {
      if (value) p.set("search", value);
      else p.delete("search");
      p.set("page", "1");
      return p;
    });

  const setStatus = (value: TMemberProjectStatus | "") =>
    setSearchParams((p) => {
      if (value) p.set("status", value);
      else p.delete("status");
      p.set("page", "1");
      return p;
    });

  const setSortBy = (sort_by: string) => setSortByState(sort_by);
  const setSortOrder = (sort_order: "asc" | "desc") =>
    setSortOrderState(sort_order);

  const params: TMemberProjectQueryParams = {
    page,
    size: LIMIT,
    search: debouncedSearch || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  };

  const activeQuery = useQuery<TMemberProjectListData>({
    queryKey: memberProjectsKeys.list(workspaceId, {
      ...params,
      is_active: true,
      status: statusParam,
    }),
    queryFn: ({ signal }) => {
      if (DEV_MOCK)
        return Promise.resolve({
          items: [],
          meta: { total: 0, page: 1, size: 10 },
        } as unknown as TMemberProjectListData);
      return getMemberProjects(
        workspaceId,
        { ...params, is_active: true },
        signal,
      );
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const archivedQuery = useQuery<TMemberProjectListData>({
    queryKey: memberProjectsKeys.list(workspaceId, {
      ...params,
      is_active: false,
      status: statusParam,
    }),
    queryFn: ({ signal }) => {
      if (DEV_MOCK)
        return Promise.resolve({
          items: [],
          meta: { total: 0, page: 1, size: 10 },
        } as unknown as TMemberProjectListData);
      return getMemberProjects(
        workspaceId,
        { ...params, is_active: false },
        signal,
      );
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const activeTotal = activeQuery.data?.meta?.total ?? 0;
  const archivedTotal = archivedQuery.data?.meta?.total ?? 0;

  const data: TMemberProject[] =
    activeTab === "active"
      ? (activeQuery.data?.items ?? [])
      : (archivedQuery.data?.items ?? []);

  const total = activeTab === "active" ? activeTotal : archivedTotal;
  const isLoading =
    activeTab === "active" ? activeQuery.isPending : archivedQuery.isPending;

  const totalPages = Math.ceil(total / LIMIT);
  const hasNext = page < totalPages;
  const hasPrev = page > 1;

  const onNext = () => setPage(page + 1);
  const onPrev = () => setPage(page - 1);

  return {
    data,
    total,
    isLoading,
    search: rawSearch,
    searchInput: rawSearch,
    setSearch,
    page,
    setPage,
    hasNext,
    hasPrev,
    onNext,
    onPrev,
    setStatus,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
    activeTab,
    setActiveTab,
    activeTotal,
    archivedTotal,
  };
}
